import React, { useState } from 'react';
import {
  Wind, Zap, Droplets, Monitor, Wrench, HomeIcon,
  Star, ChevronRight, UserCircle,
  ArrowRight, ShieldCheck, Check, X, Clock, Search,
  Camera, Calendar, Headphones, MapPin
} from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { DEFAULT_PUBLIC_SERVICES, getServiceName, statusMap, SERVICE_SCOPE, v2 } from '../constants';
import FIXIChat from '../components/FIXIChat';

// ── Design Tokens ─────────────────────────────────────────────
const C = {
  page:    '#F5F7FA',
  white:   '#FFFFFF',
  primary: '#2962FF',
  ink:     '#17202A',
  sub:     '#374151',
  muted:   '#9CA3AF',
  border:  '#E8ECF0',
  green:   '#00D68F',
  red:     '#FF4D4F',
  shadow:  '0 2px 12px rgba(0,0,0,0.07)',
};
const heroGrad = 'linear-gradient(135deg, #1648D8 0%, #2962FF 55%, #1A3FBB 100%)';

// ── 服务弹窗 ──────────────────────────────────────────────────
function ServiceScopeModal({ service, lang, onConfirm, onClose }) {
  if (!service) return null;
  const sKey = Object.keys(SERVICE_SCOPE).find(k =>
    (service.service_name || '').toLowerCase().includes(k) ||
    k.includes((service.service_name || '').toLowerCase().split(' ')[0])
  );
  const scope = sKey ? SERVICE_SCOPE[sKey]?.[lang] : null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: '24px 24px 0 0', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '12px 0 0', display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: '#CBD5E1' }} />
        </div>
        <div style={{ padding: '12px 20px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ margin: '0 0 2px', fontSize: '11px', color: C.muted, fontWeight: '800' }}>{lang === 'es' ? 'ANTES DE CONTINUAR' : '下单前请确认'}</p>
            <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '900', color: C.ink }}>{getServiceName(service.service_name, lang)}</h3>
          </div>
          <button onClick={onClose} style={{ background: '#F1F5F9', border: 'none', borderRadius: '8px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <X size={16} color="#64748B" />
          </button>
        </div>
        <div style={{ overflowY: 'auto', padding: '16px 20px', flex: 1 }}>
          {scope ? (
            <>
              <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '10px', padding: '11px 14px', marginBottom: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '12px', color: '#1D4ED8', fontWeight: '800' }}>{lang === 'es' ? 'Precio' : '服务价格'}</span>
                <b style={{ fontSize: '16px', color: C.primary }}>{scope.priceNote}</b>
              </div>
              <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '900', color: '#15803D' }}>✅ {lang === 'es' ? 'Incluido' : '服务包含'}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', marginBottom: '16px' }}>
                {scope.includes.map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>
                      <Check size={11} color="#16A34A" />
                    </div>
                    <span style={{ fontSize: '13px', color: C.ink, lineHeight: '1.45' }}>{item}</span>
                  </div>
                ))}
              </div>
              <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '900', color: '#B91C1C' }}>❌ {lang === 'es' ? 'No incluido' : '不包含'}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', marginBottom: '16px' }}>
                {scope.excludes.map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>
                      <X size={11} color="#DC2626" />
                    </div>
                    <span style={{ fontSize: '13px', color: C.ink, lineHeight: '1.45' }}>{item}</span>
                  </div>
                ))}
              </div>
              <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', padding: '12px 14px' }}>
                <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '900', color: '#92400E' }}>📌 {lang === 'es' ? 'Reglas importantes' : '重要规则'}</p>
                {scope.rules.map((r, i) => (
                  <p key={i} style={{ margin: i < scope.rules.length - 1 ? '0 0 5px' : '0', fontSize: '12px', color: '#78350F', lineHeight: '1.4' }}>• {r}</p>
                ))}
              </div>
            </>
          ) : (
            <p style={{ color: C.muted, fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>
              {lang === 'es' ? 'Servicio con cotización personalizada.' : '此服务需要定制报价。'}
            </p>
          )}
        </div>
        <div style={{ padding: '14px 20px 28px', borderTop: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button onClick={onConfirm} style={{ width: '100%', padding: '15px', borderRadius: '14px', background: heroGrad, color: '#fff', border: 'none', fontWeight: '900', fontSize: '15px', cursor: 'pointer', boxShadow: '0 4px 14px rgba(41,98,255,0.35)' }}>
            {lang === 'es' ? 'Entendido, quiero pedir →' : '已了解，立即下单 →'}
          </button>
          <button onClick={onClose} style={{ width: '100%', padding: '12px', borderRadius: '14px', background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
            {lang === 'es' ? 'Volver' : '返回'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 服务图标配置（对应设计图） ────────────────────────────────
function getServiceConfig(s, lang) {
  if (!s) return { Icon: Wrench, color: '#10B981', bg: '#E7FBF4', sub: '' };
  const n = (s.service_name || '').toLowerCase();
  if (n.includes('plom') || n.includes('水管') || n.includes('水'))
    return { Icon: Droplets, color: '#0EA5E9', bg: '#E0F7FF', sub: lang === 'es' ? 'Fugas·Tuberías' : '漏水·管道' };
  if (n.includes('elec') || n.includes('电工') || n.includes('eléctr'))
    return { Icon: Zap, color: '#F59E0B', bg: '#FFF8E0', sub: lang === 'es' ? 'Circuitos·Enchufes' : '电路·插座' };
  if (n.includes('limp') || n.includes('洁') || n.includes('clean'))
    return { Icon: HomeIcon, color: '#F97316', bg: '#FFF0E5', sub: lang === 'es' ? 'Profunda·Diaria' : '深度·日常' };
  if (n.includes('aire') || n.includes('a/c') || n.includes('空调') || n.includes('ac'))
    return { Icon: Wind, color: '#2962FF', bg: '#EBF1FF', sub: lang === 'es' ? 'Limpieza·Gas' : '清洗·加氟' };
  if (n.includes('electro') || n.includes('家电') || n.includes('nevera') || n.includes('lavadora'))
    return { Icon: Monitor, color: '#8B5CF6', bg: '#F3EEFF', sub: lang === 'es' ? 'Nevera·Lavadora' : '冰箱·洗衣机' };
  return { Icon: Wrench, color: '#10B981', bg: '#E7FBF4', sub: lang === 'es' ? 'Muebles·Luces' : '家具·灯具' };
}

// ── 主组件 ────────────────────────────────────────────────────
export default function HomeView() {
  const {
    lang, t, user, userData, orders, services,
    startServiceFlow, beginRegistrationAs, handleShare,
    setView, setFilterStatus, role, openLoginFlow, setAuthMode
  } = useAppContext();

  const visibleServices = services.length > 0 ? services : DEFAULT_PUBLIC_SERVICES;
  const [scopeService, setScopeService] = useState(null);
  const handleServiceClick = (s) => setScopeService(s);
  const handleScopeConfirm = () => { startServiceFlow(scopeService); setScopeService(null); };
  const handleScopeClose   = () => setScopeService(null);

  const activeOrder = user && role === 'client'
    ? orders.find(o => o.client_id === user.uid &&
        ['AWAITING_VERIFICATION','OPEN','ACCEPTED','ASSIGNED','WORKING','PENDING_APPROVAL'].includes(o.status))
    : null;

  // 模拟在线技师
  const mockTechs = [
    { name: lang === 'es' ? 'Carlos M.' : '张师傅', spec: lang === 'es' ? 'A/C Expert'  : '空调维修专家', rating: 5.0, exp: lang === 'es' ? '3 años' : '3年经验', dist: '2.1km', color: '#2962FF', initials: '张' },
    { name: lang === 'es' ? 'Luis P.'   : '李师傅', spec: lang === 'es' ? 'Plomero'     : '水管维修专家', rating: 4.9, exp: lang === 'es' ? '5 años' : '5年经验', dist: '1.8km', color: '#0EA5E9', initials: '李' },
    { name: lang === 'es' ? 'Jorge R.'  : '王师傅', spec: lang === 'es' ? 'Electricista': '电工安装专家', rating: 4.9, exp: lang === 'es' ? '4 años' : '4年经验', dist: '2.3km', color: '#F59E0B', initials: '王' },
  ];

  return (
    <div style={{ background: C.page, minHeight: '100vh', paddingBottom: '85px' }}>

      {/* ── 搜索栏 ─────────────────────────────────────────── */}
      <div style={{ background: C.white, padding: '10px 16px 12px' }}>
        <div style={{ background: '#F1F3F6', borderRadius: '12px', padding: '11px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Search size={16} color={C.muted} />
          <span style={{ color: C.muted, fontSize: '14px' }}>
            {lang === 'es' ? 'Buscar servicio, ej: A/C, plomero…' : '搜索服务，如：空调清洗'}
          </span>
        </div>
      </div>

      {/* ── 活跃订单提示 ────────────────────────────────────── */}
      {activeOrder && (
        <div style={{ margin: '0 16px 10px', background: '#FFF7E6', border: '1px solid #FCD34D', borderRadius: '14px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
          onClick={() => { setFilterStatus(null); setView('history'); }}>
          <span style={{ fontSize: '20px' }}>📋</span>
          <div style={{ flex: 1 }}>
            <b style={{ fontSize: '13px', color: C.ink }}>{lang === 'es' ? '⚡ Pedido activo' : '⚡ 当前订单'}</b>
            <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#B45309' }}>{statusMap[lang]?.[activeOrder.status]}</p>
          </div>
          <ChevronRight size={16} color={C.muted} />
        </div>
      )}

      {/* ── Hero Banner ─────────────────────────────────────── */}
      <div style={{ margin: '12px 16px 20px', borderRadius: '20px', overflow: 'hidden', background: heroGrad, position: 'relative', minHeight: '210px', boxShadow: '0 8px 30px rgba(41,98,255,0.28)' }}>

        {/* 左侧内容 */}
        <div style={{ padding: '22px 0 22px 20px', position: 'relative', zIndex: 2, maxWidth: '56%' }}>

          {/* 徽章 */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'rgba(255,255,255,0.20)', border: '1px solid rgba(255,255,255,0.30)', borderRadius: '20px', padding: '4px 12px 4px 8px', marginBottom: '12px' }}>
            <Zap size={11} color="#FBBF24" fill="#FBBF24" />
            <span style={{ fontSize: '11px', color: '#fff', fontWeight: '800', letterSpacing: '0.2px' }}>
              {lang === 'es' ? '30 min respuesta rápida' : '30分钟快速上门'}
            </span>
          </div>

          {/* 主标题 */}
          <h2 style={{ color: '#fff', fontSize: lang === 'es' ? '20px' : '26px', fontWeight: '900', margin: '0 0 12px', lineHeight: '1.2', letterSpacing: lang === 'zh' ? '1px' : '-0.3px' }}>
            {lang === 'es' ? 'Reparaciones\nprofesionales\na domicilio' : '专业维修 安心到家'}
          </h2>

          {/* 三勾横排 */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
            {(lang === 'es'
              ? ['Técnico cert.', 'Precio fijo', 'Garantía 30d']
              : ['持证技师', '透明报价', '30天保修']
            ).map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{ width: '15px', height: '15px', borderRadius: '50%', background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Check size={9} color="#fff" strokeWidth={3} />
                </div>
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.92)', fontWeight: '700' }}>{item}</span>
              </div>
            ))}
          </div>

          {/* CTA 按钮 — 药丸样式 */}
          <button
            onClick={() => user ? handleServiceClick(visibleServices[0]) : setAuthMode('role')}
            style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: '28px', padding: '12px 20px', fontWeight: '900', fontSize: '15px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '10px', boxShadow: '0 4px 16px rgba(0,0,0,0.22)' }}>
            <span>{lang === 'es' ? 'Reservar ahora' : '立即预约'}</span>
            <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowRight size={14} color="#fff" />
            </div>
          </button>

          {/* 头像组 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '14px' }}>
            <div style={{ display: 'flex' }}>
              {[
                { bg: '#2563EB', text: '张' },
                { bg: '#16A34A', text: '李' },
                { bg: '#F59E0B', text: '王' },
              ].map((av, i) => (
                <div key={i} style={{ width: '26px', height: '26px', borderRadius: '50%', background: av.bg, border: '2px solid rgba(255,255,255,0.5)', marginLeft: i > 0 ? '-8px' : 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '800', color: '#fff' }}>
                  {av.text}
                </div>
              ))}
            </div>
            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.88)', fontWeight: '600' }}>
              {lang === 'es' ? '128 técnicos en servicio' : '128位技师在线为您服务'}
            </span>
          </div>
        </div>

        {/* 右侧技师图 */}
        <div style={{ position: 'absolute', right: 0, bottom: 0, top: 0, width: '46%', overflow: 'hidden', pointerEvents: 'none', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          {/* 背景光圈 */}
          <div style={{ position: 'absolute', right: '-30px', bottom: '-30px', width: '180px', height: '180px', borderRadius: '50%', background: 'rgba(255,255,255,0.10)' }} />
          <div style={{ position: 'absolute', right: '10px', top: '10px', width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
          {/* 技师主图 */}
          <img
            src="/fixbox_technician_character.png"
            alt="technician"
            style={{ width: '100%', maxWidth: '180px', objectFit: 'contain', filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.25))', marginBottom: '-4px', display: 'block', userSelect: 'none' }}
          />
        </div>

        {/* 安全角标 */}
        <div style={{ position: 'absolute', top: '14px', right: '14px', background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)', borderRadius: '14px', padding: '8px 10px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.25)' }}>
          <ShieldCheck size={20} color="#fff" />
          <p style={{ margin: '3px 0 0', fontSize: '9px', color: 'rgba(255,255,255,0.92)', fontWeight: '800' }}>
            {lang === 'es' ? 'Garantía' : '安全保障'}
          </p>
        </div>
      </div>

      {/* ── 热门服务 ─────────────────────────────────────────── */}
      <div style={{ margin: '0 16px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '900', color: C.ink }}>
            {lang === 'es' ? 'Servicios populares' : '热门服务'}
          </h3>
          <button style={{ color: C.primary, fontSize: '13px', fontWeight: '700', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '2px' }}>
            {lang === 'es' ? 'Ver todo' : '全部服务'} <ChevronRight size={14} color={C.primary} />
          </button>
        </div>

        <div style={{ background: C.white, borderRadius: '18px', boxShadow: C.shadow, overflow: 'hidden' }}>
          <div style={{
            display: 'flex',
            overflowX: visibleServices.length > 4 ? 'auto' : 'visible',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            padding: '6px 8px 10px',
          }}>
            {visibleServices.map((s, i) => {
              const cfg = getServiceConfig(s, lang);
              return (
                <div key={`svc-${i}`} onClick={() => handleServiceClick(s)}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    padding: '14px 4px 10px',
                    cursor: 'pointer', borderRadius: '12px',
                    flexShrink: 0,
                    width: visibleServices.length > 4 ? '80px' : `${100 / visibleServices.length}%`,
                  }}>
                  <div style={{ width: '56px', height: '56px', borderRadius: '18px', background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' }}>
                    <cfg.Icon size={28} color={cfg.color} strokeWidth={1.6} />
                  </div>
                  <span style={{ fontSize: '12px', color: C.ink, fontWeight: '700', textAlign: 'center', lineHeight: '1.3', whiteSpace: visibleServices.length > 4 ? 'nowrap' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '72px' }}>
                    {getServiceName(s.service_name, lang)}
                  </span>
                  <span style={{ fontSize: '10px', color: C.muted, textAlign: 'center', marginTop: '3px', whiteSpace: 'nowrap' }}>
                    {cfg.sub}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── 未登录注册入口 ────────────────────────────────────── */}
      {!user && (
        <div style={{ margin: '0 16px 20px' }}>
          <div style={{ background: heroGrad, borderRadius: '20px', padding: '18px 16px', boxShadow: '0 6px 22px rgba(41,98,255,0.24)', display: 'flex', alignItems: 'center', gap: '14px' }}>
            {/* 头像圆圈 */}
            <div style={{ width: '54px', height: '54px', borderRadius: '50%', background: 'rgba(255,255,255,0.18)', border: '2px solid rgba(255,255,255,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <UserCircle size={30} color="#fff" />
            </div>
            {/* 文字 + 按钮 */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <b style={{ color: '#fff', fontSize: '15px', display: 'block', fontWeight: '900', marginBottom: '3px' }}>
                {lang === 'es' ? '¡Únete a FixBox!' : '加入 FixBox'}
              </b>
              <span style={{ color: 'rgba(255,255,255,0.78)', fontSize: '11px', display: 'block', marginBottom: '12px' }}>
                {lang === 'es' ? 'Regístrate y pide tu servicio fácil' : '注册账号，一键预约上门维修'}
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setAuthMode('role')}
                  style={{ flex: 1, padding: '9px 0', background: '#fff', color: C.primary, border: 'none', borderRadius: '22px', fontWeight: '900', fontSize: '13px', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
                  {lang === 'es' ? 'Registrarse' : '立即注册'}
                </button>
                <button onClick={() => openLoginFlow({ type: 'view', view: 'home' })}
                  style={{ flex: 1, padding: '9px 0', background: 'rgba(255,255,255,0.18)', color: '#fff', border: '1px solid rgba(255,255,255,0.38)', borderRadius: '22px', fontWeight: '800', fontSize: '13px', cursor: 'pointer' }}>
                  {lang === 'es' ? 'Iniciar sesión' : '登录'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── 快捷功能行（薄荷绿） ─────────────────────────────── */}
      <div style={{ margin: '0 16px 20px' }}>
        <div style={{ background: '#F0FDF8', border: '1px solid #C6F6E4', borderRadius: '18px', display: 'flex', overflow: 'hidden' }}>
          {[
            { Icon: Camera,    color: '#6B7280', label: lang === 'es' ? 'Diagnóstico' : '拍照诊断', sub: lang === 'es' ? 'IA detecta fallas' : 'AI识别故障',   action: () => {} },
            { Icon: Calendar,  color: '#6B7280', label: lang === 'es' ? 'Reservar'    : '立即预约', sub: lang === 'es' ? 'Agendar visita'    : '快速安排上门', action: () => handleServiceClick(visibleServices[0]) },
            { Icon: Headphones,color: '#6B7280', label: lang === 'es' ? 'Soporte'     : '在线客服', sub: lang === 'es' ? '7×24 horas'        : '7×24小时',    action: () => {} },
          ].map((item, i) => (
            <div key={i} onClick={item.action}
              style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 10px', cursor: 'pointer', borderRight: i < 2 ? '1px solid #C6F6E4' : 'none' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'rgba(255,255,255,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <item.Icon size={20} color={item.color} />
              </div>
              <div style={{ minWidth: 0 }}>
                <b style={{ fontSize: '12px', color: C.ink, display: 'block', fontWeight: '800', whiteSpace: 'nowrap' }}>{item.label}</b>
                <span style={{ fontSize: '10px', color: C.muted, whiteSpace: 'nowrap' }}>{item.sub}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── 在线技师 ─────────────────────────────────────────── */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ padding: '0 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: C.green, boxShadow: `0 0 7px ${C.green}` }} />
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '900', color: C.ink }}>
              {lang === 'es' ? 'Técnicos en línea' : '在线技师'}
            </h3>
            <span style={{ fontSize: '12px', color: C.muted }}>· 128 {lang === 'es' ? 'online' : '位技师在线'}</span>
          </div>
          <button style={{ color: C.primary, fontSize: '12px', fontWeight: '700', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '2px' }}>
            {lang === 'es' ? 'Ver todo' : '查看全部'} <ChevronRight size={13} color={C.primary} />
          </button>
        </div>

        {/* 三列技师卡（同屏显示） */}
        <div style={{ margin: '0 16px', background: C.white, borderRadius: '18px', boxShadow: C.shadow, overflow: 'hidden' }}>
          <div style={{ display: 'flex' }}>
            {mockTechs.map((tech, i) => (
              <div key={i} style={{
                flex: 1,
                padding: '14px 10px',
                borderRight: i < mockTechs.length - 1 ? `1px solid ${C.border}` : 'none',
                display: 'flex',
                flexDirection: 'column',
              }}>
                {/* 头像 + 姓名评分 */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', marginBottom: '6px' }}>
                  {/* 头像带在线绿点 */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <div style={{
                      width: '44px', height: '44px', borderRadius: '50%',
                      background: `linear-gradient(135deg, ${tech.color}22, ${tech.color}44)`,
                      border: `2px solid ${tech.color}50`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '26px', overflow: 'hidden'
                    }}>👷</div>
                    <div style={{ position: 'absolute', bottom: '1px', right: '1px', width: '10px', height: '10px', borderRadius: '50%', background: C.green, border: '2px solid #fff' }} />
                  </div>
                  {/* 姓名 + 评分 + 专业 */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px', flexWrap: 'nowrap' }}>
                      <b style={{ fontSize: '12px', color: C.ink, fontWeight: '800', whiteSpace: 'nowrap' }}>{tech.name}</b>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', background: C.primary, borderRadius: '10px', padding: '1px 4px', flexShrink: 0 }}>
                        <Star size={7} fill="#fff" color="#fff" />
                        <span style={{ fontSize: '9px', color: '#fff', fontWeight: '900' }}>{tech.rating}</span>
                      </div>
                    </div>
                    <span style={{ fontSize: '10px', color: C.muted, display: 'block', marginTop: '3px', lineHeight: '1.3' }}>{tech.spec}</span>
                  </div>
                </div>

                {/* 经验 + 距离 */}
                <div style={{ display: 'flex', gap: '6px', marginBottom: '7px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <Clock size={9} color={C.muted} />
                    <span style={{ fontSize: '9px', color: C.muted }}>{tech.exp}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <MapPin size={9} color={C.muted} />
                    <span style={{ fontSize: '9px', color: C.muted }}>{tech.dist}</span>
                  </div>
                </div>

                {/* 在线状态 */}
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ fontSize: '10px', color: C.green, fontWeight: '800' }}>
                    ● {lang === 'es' ? 'En línea' : '在线'}
                  </span>
                </div>

                {/* 预约按钮 */}
                <button onClick={() => handleServiceClick(visibleServices[0])}
                  style={{ width: '100%', padding: '8px 2px', background: heroGrad, color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '800', fontSize: '11px', cursor: 'pointer', boxShadow: '0 3px 8px rgba(41,98,255,0.22)' }}>
                  {lang === 'es' ? 'Reservar' : '立即预约'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── 推荐横幅（浅绿薄荷风格） ────────────────────────── */}
      <div style={{ margin: '0 16px 20px' }}>
        <div style={{
          background: 'linear-gradient(135deg, #F0FDF8 0%, #DCFCEF 100%)',
          border: '1px solid #BBF7D8',
          borderRadius: '20px',
          padding: '18px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          boxShadow: '0 2px 12px rgba(0,168,107,0.12)',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* 文字区 */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'rgba(0,180,100,0.12)', borderRadius: '20px', padding: '3px 10px', marginBottom: '8px' }}>
              <span style={{ fontSize: '12px' }}>🧡</span>
              <span style={{ fontSize: '11px', color: '#059669', fontWeight: '800' }}>
                {lang === 'es' ? 'REFERIDOS' : '推荐有礼'}
              </span>
            </div>
            <h3 style={{ color: C.ink, fontSize: '18px', fontWeight: '900', margin: '0 0 5px', lineHeight: '1.2' }}>
              {lang === 'es' ? 'Invita amigos:\n¡ambos $5.00!' : '邀请好友 各得 $5.00'}
            </h3>
            <p style={{ color: '#6B7280', fontSize: '11px', margin: '0 0 14px', lineHeight: '1.5' }}>
              {lang === 'es' ? 'Tu amigo completa su primer pedido y ambos ganan $5' : '好友完成首单后，您和好友都将获得 $5.00 奖励！'}
            </p>
            <button onClick={handleShare}
              style={{ background: '#059669', color: '#fff', border: 'none', borderRadius: '22px', padding: '10px 20px', fontWeight: '900', fontSize: '13px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', boxShadow: '0 3px 10px rgba(5,150,105,0.30)' }}>
              {lang === 'es' ? 'Invitar ahora' : '立即邀请'} <ChevronRight size={14} />
            </button>
          </div>

          {/* 右侧礼物图 */}
          <div style={{ flexShrink: 0, textAlign: 'center', position: 'relative' }}>
            <span style={{ fontSize: '64px', display: 'block', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))' }}>🎁</span>
            <span style={{ position: 'absolute', bottom: '-4px', right: '-4px', fontSize: '22px', transform: 'rotate(-15deg)' }}>🪙</span>
            <span style={{ position: 'absolute', bottom: '6px', left: '-8px', fontSize: '16px', transform: 'rotate(10deg)' }}>🪙</span>
          </div>
        </div>
      </div>

      {/* FIXI AI 悬浮按钮 */}
      <FIXIChat lang={lang} />

      {/* 服务说明弹窗 */}
      {scopeService && (
        <ServiceScopeModal service={scopeService} lang={lang} onConfirm={handleScopeConfirm} onClose={handleScopeClose} />
      )}
    </div>
  );
}
